import { VariantPackCostType } from '@/types';
import { z } from 'zod';

const employeeConfigSchema = z.object({
  pricing: z
    .object({
      price_floor_mode: z.enum(VariantPackCostType).nullish().catch(null),
    })
    .nullish(),
});

export type EmployeeConfigType = z.infer<typeof employeeConfigSchema>;

/**
 * An employee's configuration: wraps a validated {@link EmployeeConfigType} and exposes getters.
 *
 * @remarks
 * Transient — only {@link EmployeeConfigType} is persisted. Parse raw jsonb via
 * {@link EmployeeConfig.schema}.
 */
export class EmployeeConfig {
  private constructor(private readonly data: EmployeeConfigType) {}

  /**
   * Zod schema validating raw `employee_config` jsonb into an
   * {@link EmployeeConfig}.
   *
   * @throws {ZodError} When the payload does not match the schema.
   */
  static schema = employeeConfigSchema.transform(
    (data) => new EmployeeConfig(data),
  );

  /** An empty config, e.g. as a fallback when raw jsonb fails to parse. */
  static empty(): EmployeeConfig {
    return new EmployeeConfig({});
  }

  /** The employee's own price-floor cost basis, or `null` if unset. */
  get priceFloorMode(): VariantPackCostType | null {
    return this.data.pricing?.price_floor_mode ?? null;
  }
}
